import type { ReactNode } from "react";
import type { DealDetail } from "@edge/shared";
import { DealOverview } from "./DealOverview";
import { DealEditorTab } from "./DealEditorTab";
import { WorkDriveTab } from "./WorkDriveTab";
import { SubmissionsTab } from "./SubmissionsTab";
import { DocPortalTab } from "./DocPortalTab";
import { LmeTab } from "./LmeTab";
import { EmailTab } from "./EmailTab";
import { IntakeNoteTab } from "./IntakeNoteTab";
import { TabPanel } from "./TabPanel";

export type FocusTab =
  | "overview"
  | "deal_editor"
  | "intake_note"
  | "submissions"
  | "email"
  | "timeline"
  | "workdrive"
  | "sign"
  | "lender_match"
  | "doc_portal";

/** What each tab body receives. Tabs that need more (e.g. reload) can read more from context. */
export interface TabContext {
  dealId: string;
  detail: DealDetail | null;
  loading: boolean;
}

export interface TabDef {
  id: FocusTab;
  label: string;
  /** True if tab is shipped — false means we render a "SOON" placeholder. */
  ready: boolean;
  /** Optional. Required when `ready: true`. */
  render?: (ctx: TabContext) => ReactNode;
}

/**
 * Single source of truth for the focus-mode tab strip. Order here is the order
 * shown in the UI. To ship a new tab: import its component above, set `ready: true`,
 * and provide a `render` function. TabStrip and FocusView both read from this list.
 */
export const FOCUS_TABS: TabDef[] = [
  {
    id: "overview",
    label: "◆ OVERVIEW",
    ready: true,
    render: (ctx) => (
      <DealOverview dealId={ctx.dealId} detail={ctx.detail} loading={ctx.loading} />
    ),
  },
  {
    id: "deal_editor",
    label: "◆ EDITOR",
    ready: true,
    render: (ctx) => <DealEditorTab dealId={ctx.dealId} />,
  },
  {
    id: "email",
    label: "EMAIL",
    ready: true,
    render: (ctx) => <EmailTab dealId={ctx.dealId} />,
  },
  {
    id: "doc_portal",
    label: "DOC PORTAL",
    ready: true,
    render: (ctx) => <DocPortalTab dealId={ctx.dealId} />,
  },
  {
    id: "intake_note",
    label: "INTAKE LOG",
    ready: true,
    render: (ctx) => <IntakeNoteTab dealId={ctx.dealId} />,
  },
  {
    id: "lender_match",
    label: "LME",
    ready: true,
    render: (ctx) => <LmeTab dealId={ctx.dealId} />,
  },
  {
    id: "submissions",
    label: "SUBMISSIONS",
    ready: true,
    render: (ctx) => (
      <SubmissionsTab dealId={ctx.dealId} dealName={ctx.detail?.name} />
    ),
  },
  {
    id: "workdrive",
    label: "WORKDRIVE",
    ready: true,
    render: (ctx) => <WorkDriveTab dealId={ctx.dealId} />,
  },
  { id: "sign", label: "SIGN", ready: false },
  { id: "timeline", label: "TIMELINE", ready: false },
];

const TAB_LABELS = Object.fromEntries(FOCUS_TABS.map((t) => [t.id, t.label]));

/** Render a single tab's body, falling back to a "SOON" placeholder if not ready. */
export function renderFocusTab(active: FocusTab, ctx: TabContext): ReactNode {
  const def = FOCUS_TABS.find((t) => t.id === active);
  if (!def) {
    return <TabPanel emptyMessage="Unknown tab" empty />;
  }
  if (!def.ready || !def.render) {
    const label = TAB_LABELS[active] ?? "This tab";
    return (
      <TabPanel
        empty
        emptyMessage={`${label.replace(/^◆ /, "")} lands in Session 1c.`}
      />
    );
  }
  return def.render(ctx);
}
